import React, { FC } from 'react'
import ActionData from '../../../types/ActionData'
import { Link } from 'react-router-dom'
import { Button, Card, Col, Form, Modal, Row } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { CircularProgress } from '@material-ui/core'
import BasicUploadForm from '../../BasicUploadForm'
import { Formik } from 'formik'
import { getDomainName } from '../../../utils/envVariables'
import { UploadModalSchema } from '../../../utils/validation'

import { mainColors } from '../../../assets/colors'
import './index.css'

interface Props {
  show: boolean;
  modalHeader: string;
  readonly: boolean;
  state: any;
  uploadingDocument: boolean;
  updateApplicationItem: ActionData;
  updateApplicationCertification: ActionData;
  onClose: () => void;
  onSubmit: (values: any) => void;
}

const renderModalBody = (modalHeader: string, state: any, errors: any, readonly: boolean, onChange: (key: string, value: Array<File> | boolean) => void) => {
  const domainName = getDomainName()
  const renderCertificationUpload = (name) => (
    <div className='mt-3'>
      <BasicUploadForm
        isMulti
        isCertification
        name={name}
        placeholder='Upload a file'
        files={state[name]}
        uploadedFile={state[`${name}File`]}
        error={errors[name]}
        readonly={readonly}
        onChangeFile={onChange}
      />
    </div>
  )
  const renderHelperText = (text) => (
    <Card>
      <Card.Body style={{ background: '#f7f7f7' }}>
        <Row>
          <Form.Group as={Col} xs={12}>
            <Form.Text>{text}</Form.Text>
          </Form.Group>
        </Row>
      </Card.Body>
    </Card>
  )
  const renderDownloadTemplate = (fileName, helperText) => {
    const fileNames = Array.isArray(fileName) ? fileName : [fileName]
    return (
      <Card>
        <Card.Body style={{ background: '#f7f7f7' }}>
          <Row>
            <Form.Group as={Col} xs={12}>
              <Form.Label>Download File</Form.Label>
              {fileNames.map((name) => (
                <div className='attached-file mb-2' key={name}>
                  <FontAwesomeIcon icon={['fas', 'file-pdf']} />
                  <Link to='route' className='ml-2' onClick={(event) => { event.preventDefault(); window.open(`${domainName}/pdf/providerapplication/certification/${name}`) }}>{name}</Link>
                </div>
              ))}
              <Form.Text>{helperText}</Form.Text>
            </Form.Group>
          </Row>
        </Card.Body>
      </Card>
    )
  }
  switch (modalHeader) {
    case 'Copy of W-9':
      return <BasicUploadForm
        isMulti
        name='w9'
        placeholder='Locate a copy of your W-9'
        files={state.w9}
        uploadedFile={state.w9File}
        error={errors.w9}
        readonly={readonly}
        onChangeFile={onChange}
      />
    case 'Copy of Facility Licensure':
      return (
        <>
          <Card>
            <Card.Body style={{ background: '#f7f7f7' }}>
              <div>
                Upload a copy of all applicable licensing, certification or accreditation(s)
              </div>
            </Card.Body>
          </Card>
          <div className='mt-3'>
            {
              !readonly || !state.doesNotHaveFacilityLicense ? (
                <BasicUploadForm
                  isMulti
                  name='facilityLicense'
                  placeholder={`Locate a ${modalHeader}`}
                  files={state.facilityLicense}
                  uploadedFile={state.facilityLicenseFile}
                  error={errors.facilityLicense}
                  readonly={readonly}
                  onChangeFile={onChange}
                />
              ) : null
            }
          </div>
          <div className='mt-3' style={{ display: 'flex', justifyContent: 'center' }}>
            <Form.Check
              type='checkbox'
              label='I do not possess licenses and/or this does not apply to me or my organization.'
              checked={state.doesNotHaveFacilityLicense}
              isInvalid={!!errors.doesNotHaveFacilityLicense}
              feedback={errors.doesNotHaveFacilityLicense}
              onChange={() => onChange('doesNotHaveFacilityLicense', !state.doesNotHaveFacilityLicense)}
            />
          </div>
        </>
      )
    case 'Copy of Certificate of Insurance':
      return <BasicUploadForm
        isMulti
        name='certificateInsurance'
        placeholder={`Locate a ${modalHeader}`}
        files={state.certificateInsurance}
        uploadedFile={state.certificateInsuranceFile}
        error={errors.certificateInsurance}
        readonly={readonly}
        onChangeFile={onChange}
      />
    case 'Background Check Verification':
      return (
        <>
          <Card>
            <Card.Body style={{ background: '#f7f7f7' }}>
              <div>
                <p>
                  The Department of Health Services (DHS) Wisconsin Administrative Code DHS 12 mandates that all Managed Care Organizations (MCOs) must ensure background checks are
                  completed for people paid to provide services to the MCO’s membership.
                </p>
                <p>
                  All contracted providers are required to complete background checks on employees prior to contracting LCI requires verification that background checks are being
                  completed and an attestation letter stating all agency employees have current background checks completed within four years.
                </p>
              </div>
              <hr />
              <Row>
                <Form.Group as={Col} xs={12}>
                  <Form.Label>Attached / Sample File(s)</Form.Label>
                  <div className='attached-file'>
                    <FontAwesomeIcon icon={['fas', 'file-pdf']} />
                    <Link to='route' className='ml-2' onClick={(event) => { event.preventDefault(); window.open(`${domainName}/pdf/providerapplication/item/Background Check Request Fillable Form.pdf`) }}>Background Check Request Fillable Form.pdf</Link>
                  </div>
                </Form.Group>
              </Row>
            </Card.Body>
          </Card>
          <div className='mt-3'>
            <BasicUploadForm
              isMulti
              name='backgroundCheckVerification'
              placeholder='Locate your file to upload'
              files={state.backgroundCheckVerification}
              uploadedFile={state.backgroundCheckVerificationFiles}
              error={errors.backgroundCheckVerification}
              readonly={readonly}
              onChangeFile={onChange}
            />
          </div>
        </>
      )
    case 'Debarment Attestation Letter':
      return (
        <>
          <Card>
            <Card.Body style={{ background: '#f7f7f7' }}>
              <div>
                <p>
                  LCI requires all contracted providers complete a debarment attestation letter. Providers must ensure that neither its owners, employees or subcontracts are debarred
                  (excluded from participation in any federal health care programs).
                </p>
                <p>
                  Please include an attestation letter stating that your agency has and follow its debarment policy.
                </p>
              </div>
              <hr />
              <Row>
                <Form.Group as={Col} xs={12}>
                  <Form.Label>Sample File</Form.Label>
                  <div className='attached-file'>
                    <FontAwesomeIcon icon={['fas', 'file-pdf']} />
                    <Link to='route' className='ml-2' onClick={(event) => { event.preventDefault(); window.open(`${domainName}/pdf/providerapplication/item/Sample-Debarment-Attestation-Letter.doc`) }}>Sample-Debarment-Attestation-Letter.doc</Link>
                  </div>
                </Form.Group>
              </Row>
            </Card.Body>
          </Card>
          <div className='mt-3'>
            <BasicUploadForm
              isMulti
              name='debarmentAttestationLetter'
              placeholder='Locate your file to upload'
              files={state.debarmentAttestationLetter}
              uploadedFile={state.debarmentAttestationLetterFile}
              error={errors.debarmentAttestationLetter}
              readonly={readonly}
              onChangeFile={onChange}
            />
          </div>
        </>
      )
    case 'Training Attestation Letter':
      return (
        <>
          <Card>
            <Card.Body style={{ background: '#f7f7f7' }}>
              <div>
                <p>
                  LCI requires all contracted providers attest that your agency provides standards, training and competency for staff prior to contracting.
                </p>
                <p>
                  Please upload a copy of your training attestation letter.
                </p>
              </div>
              <hr />
              <Row>
                <Form.Group as={Col} xs={12}>
                  <Form.Label>Sample File</Form.Label>
                  <div className='attached-file'>
                    <FontAwesomeIcon icon={['fas', 'file-pdf']} />
                    <Link to='route' className='ml-2' onClick={(event) => { event.preventDefault(); window.open(`${domainName}/pdf/providerapplication/item/Sample Training Attestation Letter.doc`) }}>Sample Training Attestation Letter.doc</Link>
                  </div>
                </Form.Group>
              </Row>
            </Card.Body>
          </Card>
          <div className='mt-3'>
            <BasicUploadForm
              isMulti
              name='trainingAttestationLetter'
              placeholder='Locate your file to upload'
              files={state.trainingAttestationLetter}
              uploadedFile={state.trainingAttestationLetterFile}
              error={errors.trainingAttestationLetter}
              readonly={readonly}
              onChangeFile={onChange}
            />
          </div>
        </>
      )
    case 'Civil Rights Compliance Attestation Letter':
      return (
        <>
          <Card>
            <Card.Body style={{ background: '#f7f7f7' }}>
              <div>
                <p>
                  LCI requires all contracted providers to provide a Civil Rights Compliance Attestation Letter. Please upload a copy of your Civil Rights Attestation Letter.
                </p>
                <p>
                  For more information on Civil Rights see: http://dhs.wisconsin.gov/civilrights/Index.html
                </p>
              </div>
              <hr />
              <Row>
                <Form.Group as={Col} xs={12}>
                  <Form.Label>Attached File</Form.Label>
                  <div className='attached-file'>
                    <FontAwesomeIcon icon={['fas', 'file-pdf']} />
                    <Link to='route' className='ml-2' onClick={(event) => { event.preventDefault(); window.open(`${domainName}/pdf/providerapplication/item/Sample-CRC-Attestation-Letter.doc`) }}>Sample-CRC-Attestation-Letter.doc</Link>
                  </div>
                </Form.Group>
              </Row>
            </Card.Body>
          </Card>
          <div className='mt-3'>
            {
              !readonly || !state.doesNotApply ? (
                <BasicUploadForm
                  isMulti
                  name='civilRightsComplianceAttestationLetter'
                  placeholder='Locate your file to upload'
                  files={state.civilRightsComplianceAttestationLetter}
                  uploadedFile={state.civilRightsComplianceAttestationLetterFile}
                  error={errors.civilRightsComplianceAttestationLetter}
                  readonly={readonly}
                  onChangeFile={onChange}
                />
              ) : null
            }
          </div>
          <div className='mt-3' style={{ display: 'flex', justifyContent: 'center' }}>
            <Form.Check
              type='checkbox'
              label='Does Not Apply (organization has fewer than 25 employees)'
              checked={state.doesNotApply}
              onChange={() => onChange('doesNotApply', !state.doesNotApply)}
            />
          </div>
        </>
      )
    case 'Residential Worksheets':
      return (
        <>
          <Card>
            <Card.Body style={{ background: '#f7f7f7' }}>
              <div>
                Please download to fill out the attached form and upload completed file.
              </div>
              <hr />
              <Row>
                <Form.Group as={Col} xs={12}>
                  <Form.Label>Worksheet</Form.Label>
                  <div className='attached-file'>
                    <FontAwesomeIcon icon={['fas', 'file-pdf']} />
                    <Link to='route' className='ml-2' onClick={(event) => { event.preventDefault(); window.open(`${domainName}/pdf/providerapplication/item/Residential Budget Sheet.xlsx`) }}>Residential Budget Sheet.xlsx</Link>
                  </div>
                </Form.Group>
              </Row>
            </Card.Body>
          </Card>
          <div className='mt-3'>
            {
              !readonly || !state.residentialWorksheetDoesNotApply ? (
                <BasicUploadForm
                  isMulti
                  name='residentialWorksheet'
                  placeholder='Locate your file to upload'
                  files={state.residentialWorksheet}
                  uploadedFile={state.residentialWorksheetFile}
                  error={errors.residentialWorksheet}
                  readonly={readonly}
                  onChangeFile={onChange}
                />
              ) : null
            }
          </div>
          <div className='mt-3' style={{ display: 'flex', justifyContent: 'center' }}>
            <Form.Check
              type='checkbox'
              label='This does not apply to my Location / Organization'
              checked={state.residentialWorksheetDoesNotApply}
              onChange={() => onChange('residentialWorksheetDoesNotApply', !state.residentialWorksheetDoesNotApply)}
            />
          </div>
        </>
      )
    case 'Certification Application':
      return (
        <>
          {renderDownloadTemplate('Certification Application.docx', 'Download this file and fill it out. Then upload the completed file below.')}
          {renderCertificationUpload('certificationApplication')}
        </>
      )
    case 'Individual Questionnaire':
      return (
        <>
          {renderDownloadTemplate('Individual questionnaire Private Certification.docx', 'Download this file and fill it out. Then upload the completed file below.')}
          {renderCertificationUpload('individualQuestionnaire')}
        </>
      )
    case 'Owner/Operator Background Check (BID)':
      return (
        <>
          <Card>
            <Card.Body style={{ background: '#f7f7f7' }}>
              <Row>
                <Form.Group as={Col} xs={12}>
                  <Form.Label>Download File</Form.Label>
                  <div className='attached-file mb-2'>
                    <FontAwesomeIcon icon={['fas', 'file-pdf']} />
                    <Link to='route' className='ml-2' onClick={(event) => { event.preventDefault(); window.open(`${domainName}/pdf/providerapplication/certification/Background-Check-(BID).pdf`) }}>Background-Check-(BID).pdf</Link>
                  </div>
                  <Form.Text className='form-text-spacing'>In compliance with the August 2024 Standards for Certified 1-2 Bed Adult Family Homes, Caregiver Background Checks for Certified AFH staff must be completed every 3 years.</Form.Text>
                  <Form.Text className='form-text-spacing'>For Owner Operated AFHs (Household members only providing services) please submit a BID form for all household members over 18 years of age.</Form.Text>
                  <Form.Text className='form-text-spacing' style={{ fontWeight: 'bold' }}>For Corporate Adult Family Homes (AFHs) with hired staff:</Form.Text>
                  <Form.Text className='form-text-spacing'>Please submit the following documentation for <span style={{ fontWeight: 'bold' }}>all employees</span>, including the <span style={{ fontWeight: 'bold' }}>owner/operator</span> of the home:</Form.Text>
                  <ul className='form-text-spacing'>
                    <li>All completed Background Information Disclosure (BID) forms</li>
                    <li>Results of caregiver background checks</li>
                    <li>Results of Department of Justice (DOJ) background checks</li>
                  </ul>
                  <Form.Text className='form-text-spacing'>Be sure to include documentation for <span style={{ fontWeight: 'bold' }}>all years</span> in which background checks were completed.</Form.Text>
                </Form.Group>
              </Row>
            </Card.Body>
          </Card>
          <div className='mt-3'>
            <BasicUploadForm
              isMulti
              isCertification
              name='backgroundCheck'
              placeholder='Upload a file'
              files={state.backgroundCheck}
              uploadedFile={state.backgroundCheckFile}
              error={errors.backgroundCheck}
              readonly={readonly}
              onChangeFile={onChange}
            />
          </div>
        </>
      )
    case 'Proof of Insurance':
      return (
        <>
          {renderDownloadTemplate('Insurance requirements.docx', 'Download this file to see the required insurance coverage for Adult Family Home providers. Upload a copy of insurance coverage.')}
          {renderCertificationUpload('proofOfInsurance')}
        </>
      )
    case 'Personal Reference Form':
      return (
        <>
          {renderDownloadTemplate('Personal Reference Form.docx', 'Download this file and fill it out. Then upload the completed file below.')}
          {renderCertificationUpload('referencesForm')}
        </>
      )
    case 'HCBS Compliance Documentation':
      return (
        <>
          {renderDownloadTemplate(['HCBS Adult Residential Provider Assessment.pdf', 'DHS Certification Standards Benchmark Guide for 1-2 Bed AFH.pdf'], 'Download the attached Assessment form and complete all applicable sections. The Benchmark Guide has been included to assist you in completing the Assessment. Once completed, upload the Assessment form.')}
          {renderCertificationUpload('hcbsComplianceDocumentation')}
        </>
      )
    case 'Mortgage or Lease Agreement':
      return (
        <>
          {renderHelperText('Submit a mortgage or lease agreement with a written agreement from the property owner.')}
          {renderCertificationUpload('leaseAgreement')}
        </>
      )
    case 'Program Statement':
      return (
        <>
          {renderDownloadTemplate('Program Statement.docx', 'Providers may use the template available or upload their own program statement.')}
          {renderCertificationUpload('programStatement')}
        </>
      )
    case 'Emergency Evacuation Plan':
      return (<>
        {renderDownloadTemplate('Evacuation & Emergency Preparedness Plan.docx', 'All certified AFHs shall have a written evacuation and emergency preparedness plan in place and updated annually. Please upload the completed plan.')}
        {renderCertificationUpload('emergencyEvacuationPlan')}
      </>)
    case 'Succession Planning':
      return (
        <>
          {renderHelperText('Provider must have a written, functional back-up plan that ensures a qualified substitute operator will be available when the operator or any required staff are unexpectedly absent from or unavailable to provide required services and supports in the corporate AFH. For Owner Operated AFHs such a plan may include another household member, as long as that member is qualified to provide all services and support.  This does not apply to short-term absences by the operator for routine errands or appointments if the member does not require full-time supervision.')}
          {renderCertificationUpload('successionPlanning')}
        </>
      )
    case 'Health Attestation':
      return (
        <>
          {renderDownloadTemplate('Sample Corporate Staff Health Assessment Attestation Letter.doc', 'Providers may use the template available or upload their own health attestation letter.')}
          {renderCertificationUpload('healthAttestation')}
        </>
      )
    case 'Health Assessment':
      return (
        <>
          {renderDownloadTemplate('Household Member Health Assessment.docx', 'Select N/A for non-owner-operated homes. For owner-operated homes, this form must be completed for the owner/operator and all household members age 18 or older.')}
          {
            !readonly || !state.healthAssessmentNotApplicable ? renderCertificationUpload('healthAssessment') : null
          }
          <div className='mt-3' style={{ display: 'flex', justifyContent: 'center' }}>
            <Form.Check
              type='checkbox'
              label='This does not apply to my Location / Organization'
              checked={state.healthAssessmentNotApplicable}
              onChange={() => onChange('healthAssessmentNotApplicable', !state.healthAssessmentNotApplicable)}
            />
          </div>
        </>
      )
    case 'Driver\'s License Attestation':
      return (
        <>
          {renderDownloadTemplate('Sample Corporate Driver\'s License Attestation Letter.doc', 'Providers may use the template available or upload their own driver\'s license attestation letter.')}
          {renderCertificationUpload('driversLicenseAttestation')}
        </>
      )
    case 'Furnace Inspection':
      return (
        <>
          {renderHelperText('Please upload signed inspection form from a licensed heating contractor. To be completed every 4 years.')}
          {renderCertificationUpload('furnaceInspection')}
        </>
      )
    case 'Fire Inspection':
      return (
        <>
          {renderDownloadTemplate('Fire Inspection Checklist.docx', 'Please upload the checklist template available, completed and signed by your fire inspector, or the form provided to you by the certified fire inspector.')}
          {renderCertificationUpload('fireInspection')}
        </>
      )
    case 'Fire Extinguisher Invoice':
      return (
        <>
          {renderHelperText('All certified AFHs shall have a fire extinguisher on every floor that has a minimum rating of 2A, 10-B-C rating. Please upload the fire extinguisher inspection receipt from a certified fire extinguisher inspector, showing that all fire extinguishers were inspected and tagged to be good for the next 12 months. Alternatively, if you wish to just purchase new fire extinguishers every year (instead of getting them inspected and tagged), please upload the receipt showing this purchase.')}
          {renderCertificationUpload('fireExtinguisherInvoice')}
        </>
      )
    case 'Well Test':
      return (
        <>
          {renderHelperText('If your AFH does not have a public water supply available and receives water from a well, please schedule a well water inspection from a licensed water inspection laboratory annually. Please upload the inspection report. Please also note that this inspection will need to be completed annually and results will need to be forwarded during future yearly recertifications.')}
          {
            !readonly || !state.wellTestNotApplicable ? renderCertificationUpload('wellTest') : null
          }
          <div className='mt-3' style={{ display: 'flex', justifyContent: 'center' }}>
            <Form.Check
              type='checkbox'
              label='This does not apply to my Location / Organization'
              checked={state.wellTestNotApplicable}
              onChange={() => onChange('wellTestNotApplicable', !state.wellTestNotApplicable)}
            />
          </div>
        </>
      )
    case 'Fire Diagram':
      return (
        <>
          {renderDownloadTemplate('Fire Evacuation Plan.docx', 'Provider can use the templates available here or upload their own floor plan to include square footage of each room, as well as the locations of fire extinguishers, and smoke/carbon monoxide detectors. Please include a place where all members will meet outside the home in case of fire.')}
          {renderCertificationUpload('fireDiagram')}
        </>
      )
    case 'Government Issued Photo ID':
      return renderCertificationUpload('governmentIssuedPhotoId')
    case 'Pet Vaccinations':
      return (
        <>
          {renderHelperText('If your AFH will have pets living on-site, please upload the veterinary records showing that the rabies vaccination is up to date and include your pet policy.')}
          {
            !readonly || !state.petVaccinationsNotApplicable ? renderCertificationUpload('petVaccinations') : null
          }
          <div className='mt-3' style={{ display: 'flex', justifyContent: 'center' }}>
            <Form.Check
              type='checkbox'
              label='This does not apply to my Location / Organization'
              checked={state.petVaccinationsNotApplicable}
              onChange={() => onChange('petVaccinationsNotApplicable', !state.petVaccinationsNotApplicable)}
            />
          </div>
        </>
      )
    case 'Prescription Medication Storage Policy':
      return (
        <>
          {renderHelperText('Please upload your policy regarding medication storage within the home.')}
          {renderCertificationUpload('prescriptionMedicationStoragePolicy')}
        </>
      )
    case 'Medication Administration Policy':
      return (
        <>
          {renderHelperText('Please upload your policy regarding medication administration within the home.')}
          {renderCertificationUpload('medicationAdministrationPolicy')}
        </>
      )
    case 'Training Plan Policy and Procedure':
      return (
        <>
          {renderHelperText('Please upload policy and procedure for how the home will remain compliant with the training standards on an annual basis. Please reference the Adult Family Home Certification Standards for more information.')}
          {renderCertificationUpload('trainingPlan')}
        </>
      )
    case 'Proof of Current Training':
      return (
        <>
          {renderHelperText('Please upload proof that the Operator, substitute provider, and all other staff who provide services in the home who meet the definition of a caregiver have completed a minimum of 15 hours of up-to-date training.')}
          {renderCertificationUpload('proofOfCurrentTraining')}
        </>
      )
    case 'Proof of Finances':
      return (
        <>
          {renderHelperText('The operator is requested to present evidence of having or having access to sufficient financial reserves to meet the needs of all residents and members of the household for whom the sponsor is financially responsible and to ensure the adequate functioning of the home for a period of at least 30 days without receiving payment for the care of any resident.')}
          {renderCertificationUpload('proofOfFinances')}
        </>
      )
    case 'Additional Documents':
      return (
        <>
          <Card>
            <Card.Body style={{ background: '#f7f7f7' }}>
              <div>
                <p>This section can be used to upload additional documents you would like to share with Lakeland Care.</p>
                <p>Examples: Home and Community Based Waiver approval letters, Program Statements, Quality Remediation documentation.</p>
              </div>
            </Card.Body>
          </Card>
          <div className='mt-3'>
            {
              !readonly || !state.additionalDocumentsDoesNotApply ? (
                <BasicUploadForm
                  isMulti
                  name='additionalDocuments'
                  placeholder='Locate your file to upload'
                  files={state.additionalDocuments}
                  uploadedFile={state.additionalDocumentsFile}
                  error={errors.additionalDocuments}
                  readonly={readonly}
                  onChangeFile={onChange}
                />
              ) : null
            }
          </div>
          <div className='mt-3' style={{ display: 'flex', justifyContent: 'center' }}>
            <Form.Check
              type='checkbox'
              label='This does not apply to my Location / Organization'
              checked={state.additionalDocumentsDoesNotApply}
              onChange={() => onChange('additionalDocumentsDoesNotApply', !state.additionalDocumentsDoesNotApply)}
            />
          </div>
        </>
      )
    default:
      return <div>Should not be here</div>
  }
}

const UploadModal: FC<Props> = ({ show, modalHeader, readonly, state, uploadingDocument, updateApplicationItem, updateApplicationCertification, onClose, onSubmit }: Props) => {
  const loadingIcon = updateApplicationItem.loading || updateApplicationCertification.loading ? <CircularProgress size={70} /> :
    updateApplicationItem.error !== false || updateApplicationCertification.error !== false ? <FontAwesomeIcon icon={['fas', 'exclamation-circle']} color={mainColors.loadingBlue} className='modal-success-icon' /> : ''
  const modalText = updateApplicationItem.error !== false || updateApplicationCertification.error !== false ? 'The data could not be saved.' : ''

  return (
    <Modal size='lg' show={show} onHide={onClose}>
      <Modal.Header closeButton id='modal-header'>
        <FontAwesomeIcon icon={['fas', 'file-alt']} size='lg' className='modal-header-icon' />
        <Modal.Title id='modal-header-title'>{modalHeader === 'Copy of Facility Licensure' && state.applicationType === 'non-residential' ? 'Copy of Agency Licensure' : modalHeader}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {uploadingDocument ? (
          updateApplicationItem.loading || updateApplicationItem.error !== false ? (
            <div className='modal-loading-container'>
              {loadingIcon}
              <span className='modal-loading-text' style={updateApplicationItem.error !== false ? { width: '100%' } : {}}>
                {modalText}
                {
                  updateApplicationItem.error !== false || updateApplicationCertification.error !== false ? (<span style={{ width: '100%' }} dangerouslySetInnerHTML={{ __html: updateApplicationItem.error.response ? updateApplicationItem.error.response.text : (updateApplicationCertification.error.response ? updateApplicationCertification.error.response.text : '') }} />) : null
                }
              </span>
            </div>
          ) : updateApplicationItem.success || updateApplicationCertification.success ? (
            <div>
              <div className='modal-success-container'>
                <FontAwesomeIcon icon={['far', 'check-circle']} color={mainColors.successGreen} className='modal-success-icon' />
                <span className='modal-loading-text'>Success!</span>
                <span className='modal-success-text'>You have submitted a document.</span>
              </div>
            </div>
          ) : (
            <div></div>
          )
        ) : (
          <Formik
            initialValues={state}
            enableReinitialize
            validationSchema={UploadModalSchema}
            onSubmit={(values, { setSubmitting }) => {
              setSubmitting(false)
              onSubmit(values)
            }}
          >
            {({
              values,
              errors,
              handleSubmit,
              setFieldValue,
            }) => {
              return (
                <Form noValidate onSubmit={handleSubmit}>
                  {
                    renderModalBody(modalHeader, values, errors, readonly, setFieldValue)
                  }
                  <div style={{ display: 'flex', marginTop: 15, justifyContent: 'center' }}>
                    <Button
                      variant='secondary'
                      className='mr-2 custom-button'
                      onClick={onClose}
                    >
                      Cancel
                    </Button>
                    {
                      !readonly ? (
                        <Button
                          variant='info'
                          className='custom-button'
                          type='submit'
                        >
                          Submit
                        </Button>
                      ) : null
                    }
                  </div>
                </Form>
              )
            }}
          </Formik>
        )}

      </Modal.Body>
    </Modal>
  )
}

export default UploadModal